import { useState, useCallback } from 'react';
import { Plus, Loader2, CheckCircle, XCircle, Clock, Trash2, Edit, ExternalLink } from 'lucide-react';
import { api } from '../../services/api';
import { useNotification } from '../../contexts/NotificationContext';
import { Modal, ConfirmDialog } from '../ui';
import { useApiEffect } from '../../hooks';
import type { VistoriaVeiculo, VistoriaVeiculoCreate, VistoriaVeiculoResumo, StatusVistoria, PendenciaVistoria } from '../../types';
import { formatDate } from '../../utils/format';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  veiculoId: number;
  veiculoNome: string;
}

const TIPOS = ['Cautelar', 'Transferência', 'Laudo Mecânico', 'Vistoria DETRAN', 'Outros'];

const statusColors: Record<StatusVistoria, string> = {
  PENDENTE: 'bg-yellow-100 text-yellow-700',
  APROVADO: 'bg-green-100 text-green-700',
  REPROVADO: 'bg-red-100 text-red-700',
};

const statusIcons: Record<StatusVistoria, typeof CheckCircle> = {
  PENDENTE: Clock,
  APROVADO: CheckCircle,
  REPROVADO: XCircle,
};

const emptyForm: VistoriaVeiculoCreate = {
  tipo: 'Cautelar',
  empresa: '',
  numeroLaudo: '',
  dataVistoria: new Date().toISOString().split('T')[0],
  dataValidade: '',
  status: 'PENDENTE',
  observacoes: '',
  pendencias: [],
  arquivoUrl: '',
};

export function VistoriasVeiculoModal({ isOpen, onClose, veiculoId, veiculoNome }: Props) {
  const { showSuccess, showError, showApiError } = useNotification();

  const [vistorias, setVistorias] = useState<VistoriaVeiculo[]>([]);
  const [resumo, setResumo] = useState<VistoriaVeiculoResumo | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState<VistoriaVeiculoCreate>(emptyForm);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [novaPendencia, setNovaPendencia] = useState('');

  const loadData = useCallback(async (signal: AbortSignal) => {
    if (!veiculoId) return;
    setIsLoading(true);

    try {
      const [vistoriasRes, resumoRes] = await Promise.all([
        api.get<VistoriaVeiculo[]>(`/veiculos/${veiculoId}/vistorias`, { signal }),
        api.get<VistoriaVeiculoResumo>(`/veiculos/${veiculoId}/vistorias/resumo`, { signal }),
      ]);
      setVistorias(vistoriasRes.data);
      setResumo(resumoRes.data);
    } finally {
      setIsLoading(false);
    }
  }, [veiculoId]);

  const { refetch } = useApiEffect(loadData, [veiculoId], { enabled: isOpen && !!veiculoId });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.tipo.trim() || !form.dataVistoria) {
      showError('Tipo e Data são obrigatórios');
      return;
    }

    setIsSubmitting(true);
    try {
      if (editingId) {
        await api.put(`/veiculos/${veiculoId}/vistorias/${editingId}`, form);
        showSuccess('Vistoria atualizada');
      } else {
        await api.post(`/veiculos/${veiculoId}/vistorias`, form);
        showSuccess('Vistoria cadastrada');
      }
      setShowForm(false);
      setEditingId(null);
      setForm(emptyForm);
      refetch();
    } catch (error) {
      showApiError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = (vistoria: VistoriaVeiculo) => {
    setEditingId(vistoria.id);
    setForm({
      tipo: vistoria.tipo,
      empresa: vistoria.empresa || '',
      numeroLaudo: vistoria.numeroLaudo || '',
      dataVistoria: vistoria.dataVistoria.split('T')[0],
      dataValidade: vistoria.dataValidade?.split('T')[0] || '',
      status: vistoria.status,
      observacoes: vistoria.observacoes || '',
      pendencias: vistoria.pendencias || [],
      arquivoUrl: vistoria.arquivoUrl || '',
    });
    setShowForm(true);
  };

  const handleDelete = async () => {
    if (!deletingId) return;
    try {
      await api.delete(`/veiculos/${veiculoId}/vistorias/${deletingId}`);
      showSuccess('Vistoria excluída');
      setShowDeleteDialog(false);
      setDeletingId(null);
      refetch();
    } catch (error) {
      showApiError(error);
    }
  };

  const addPendencia = () => {
    if (!novaPendencia.trim()) return;
    const pendencia: PendenciaVistoria = {
      descricao: novaPendencia.trim(),
      resolvida: false,
    };
    setForm({ ...form, pendencias: [...(form.pendencias || []), pendencia] });
    setNovaPendencia('');
  };

  const removePendencia = (index: number) => {
    const newPendencias = [...(form.pendencias || [])];
    newPendencias.splice(index, 1);
    setForm({ ...form, pendencias: newPendencias });
  };

  const togglePendenciaResolvida = (index: number) => {
    const newPendencias = [...(form.pendencias || [])];
    newPendencias[index] = {
      ...newPendencias[index],
      resolvida: !newPendencias[index].resolvida,
      dataResolucao: !newPendencias[index].resolvida ? new Date().toISOString() : undefined,
    };
    setForm({ ...form, pendencias: newPendencias });
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={`Vistorias - ${veiculoNome}`} size="xl">
      <div className="space-y-4">
        {/* Resumo */}
        {resumo && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 p-4 bg-gray-50 rounded-lg">
            <div>
              <p className="text-sm text-gray-500">Total</p>
              <p className="text-lg font-semibold">{resumo.totalVistorias}</p>
            </div>
            <div>
              <p className="text-sm text-yellow-600">Pendentes</p>
              <p className="text-lg font-semibold text-yellow-600">{resumo.vistoriasPendentes}</p>
            </div>
            <div>
              <p className="text-sm text-green-600">Aprovadas</p>
              <p className="text-lg font-semibold text-green-600">{resumo.vistoriasAprovadas}</p>
            </div>
            <div>
              <p className="text-sm text-red-600">Reprovadas</p>
              <p className="text-lg font-semibold text-red-600">{resumo.vistoriasReprovadas}</p>
            </div>
          </div>
        )}

        {/* Header */}
        <div className="flex justify-end">
          <button
            onClick={() => {
              setShowForm(true);
              setEditingId(null);
              setForm(emptyForm);
            }}
            className="flex items-center gap-2 px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
          >
            <Plus className="w-4 h-4" />
            Nova Vistoria
          </button>
        </div>

        {/* Form */}
        {showForm && (
          <form onSubmit={handleSubmit} className="p-4 bg-blue-50 rounded-lg border border-blue-200 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tipo *</label>
                <select
                  value={form.tipo}
                  onChange={(e) => setForm({ ...form, tipo: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  {TIPOS.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <select
                  value={form.status}
                  onChange={(e) => setForm({ ...form, status: e.target.value as StatusVistoria })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="PENDENTE">Pendente</option>
                  <option value="APROVADO">Aprovado</option>
                  <option value="REPROVADO">Reprovado</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Empresa</label>
                <input
                  type="text"
                  value={form.empresa}
                  onChange={(e) => setForm({ ...form, empresa: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Nome da empresa"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Número do Laudo</label>
                <input
                  type="text"
                  value={form.numeroLaudo}
                  onChange={(e) => setForm({ ...form, numeroLaudo: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Data da Vistoria *</label>
                <input
                  type="date"
                  value={form.dataVistoria}
                  onChange={(e) => setForm({ ...form, dataVistoria: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Validade</label>
                <input
                  type="date"
                  value={form.dataValidade}
                  onChange={(e) => setForm({ ...form, dataValidade: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">URL do Arquivo</label>
              <input
                type="url"
                value={form.arquivoUrl}
                onChange={(e) => setForm({ ...form, arquivoUrl: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="https://..."
              />
            </div>

            {/* Pendências */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Pendências</label>
              <div className="flex gap-2 mb-2">
                <input
                  type="text"
                  value={novaPendencia}
                  onChange={(e) => setNovaPendencia(e.target.value)}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Adicionar pendência..."
                  onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addPendencia())}
                />
                <button
                  type="button"
                  onClick={addPendencia}
                  className="px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>
              {form.pendencias && form.pendencias.length > 0 && (
                <div className="space-y-1">
                  {form.pendencias.map((p, index) => (
                    <div key={index} className="flex items-center gap-2 p-2 bg-white rounded border">
                      <input
                        type="checkbox"
                        checked={p.resolvida}
                        onChange={() => togglePendenciaResolvida(index)}
                        className="w-4 h-4 rounded border-gray-300"
                      />
                      <span className={`flex-1 ${p.resolvida ? 'line-through text-gray-400' : ''}`}>
                        {p.descricao}
                      </span>
                      <button
                        type="button"
                        onClick={() => removePendencia(index)}
                        className="p-1 text-red-600 hover:bg-red-50 rounded"
                      >
                        <XCircle className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Observações</label>
              <textarea
                value={form.observacoes}
                onChange={(e) => setForm({ ...form, observacoes: e.target.value })}
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  setShowForm(false);
                  setEditingId(null);
                  setForm(emptyForm);
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {isSubmitting && <Loader2 className="w-4 h-4 animate-spin" />}
                {editingId ? 'Salvar' : 'Criar'}
              </button>
            </div>
          </form>
        )}

        {/* List */}
        <div className="space-y-2 max-h-[400px] overflow-y-auto">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
            </div>
          ) : vistorias.length === 0 ? (
            <p className="text-center py-8 text-gray-500">Nenhuma vistoria cadastrada</p>
          ) : (
            vistorias.map((vistoria) => {
              const StatusIcon = statusIcons[vistoria.status];
              const pendenciasResolvidas = vistoria.pendencias?.filter((p) => p.resolvida).length || 0;
              const totalPendencias = vistoria.pendencias?.length || 0;

              return (
                <div
                  key={vistoria.id}
                  className={`p-4 border rounded-lg ${
                    vistoria.status === 'APROVADO' ? 'bg-green-50 border-green-200' : vistoria.status === 'REPROVADO' ? 'bg-red-50 border-red-200' : 'bg-white'
                  }`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <StatusIcon className={`w-4 h-4 ${statusColors[vistoria.status].replace('bg-', 'text-').replace('-100', '-600')}`} />
                        <span className={`px-2 py-0.5 text-xs rounded font-medium ${statusColors[vistoria.status]}`}>
                          {vistoria.status}
                        </span>
                        <span className="text-sm font-medium text-gray-700">{vistoria.tipo}</span>
                      </div>
                      <div className="flex flex-wrap gap-4 text-sm text-gray-500">
                        {vistoria.empresa && <span>Empresa: {vistoria.empresa}</span>}
                        {vistoria.numeroLaudo && <span>Laudo: {vistoria.numeroLaudo}</span>}
                        <span>Data: {formatDate(vistoria.dataVistoria)}</span>
                        {vistoria.dataValidade && <span>Validade: {formatDate(vistoria.dataValidade)}</span>}
                        {totalPendencias > 0 && (
                          <span className={pendenciasResolvidas === totalPendencias ? 'text-green-600' : 'text-yellow-600'}>
                            Pendências: {pendenciasResolvidas}/{totalPendencias}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      {vistoria.arquivoUrl && (
                        <a
                          href={vistoria.arquivoUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-1.5 text-blue-600 hover:bg-blue-50 rounded"
                          title="Ver arquivo"
                        >
                          <ExternalLink className="w-4 h-4" />
                        </a>
                      )}
                      <button
                        onClick={() => handleEdit(vistoria)}
                        className="p-1.5 text-blue-600 hover:bg-blue-50 rounded"
                        title="Editar"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => {
                          setDeletingId(vistoria.id);
                          setShowDeleteDialog(true);
                        }}
                        className="p-1.5 text-red-600 hover:bg-red-50 rounded"
                        title="Excluir"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      <ConfirmDialog
        isOpen={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        onConfirm={handleDelete}
        type="danger"
        title="Excluir Vistoria"
        message="Tem certeza que deseja excluir esta vistoria?"
        confirmText="Excluir"
      />
    </Modal>
  );
}
